import { type ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import { useSnapshot } from 'valtio';
import Checkbox from '~/components2/forms/Checkbox';
import { AUTO_COL_WIDTH, TableV2 } from '~/components2/Table';
import store from '../../../../store';

interface AgencyFeeItem {
  id: string;
  name: string;
  averageMapFeePerPage: number;
  mapFeeNote: string;
}

const columns: ColumnDef<AgencyFeeItem>[] = [
  {
    id: 'name',
    header: 'Agency',
    accessorKey: 'name',
    size: AUTO_COL_WIDTH,
  },
  {
    header: 'Note',
    size: 400,
    cell: (cell) => {
      const { averageMapFeePerPage, mapFeeNote } = cell.row.original;

      return (
        <div className="space-y-2 text-left">
          {mapFeeNote && <div>{mapFeeNote}</div>}
          {!averageMapFeePerPage ? (
            <div>Exact fee not known at this time.</div>
          ) : (
            <div>Average fee per page: ${averageMapFeePerPage}</div>
          )}
        </div>
      );
    },
  },
];

export default function FeeConfirmation() {
  const { agencies, formState, constraints, selectedAgencyIds } = useSnapshot(store);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const data = useMemo(() => {
    const data: AgencyFeeItem[] = [];
    selectedAgencyIds.forEach((agencyId) => {
      const a = agencies[agencyId];
      if (a.chargesMapFees) {
        data.push({
          id: a.agencyId,
          name: a.agencyName,
          averageMapFeePerPage: a.averageMapFeePerPage,
          mapFeeNote: a.mapFeeNote,
        });
      }
    });
    return data;
  }, [agencies]);

  const feeTable = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isEmpty(data)) {
    return null;
  }

  if (!constraints.hasFeeConstraints) {
    return null;
  }

  return (
    <>
      <Checkbox
        data-testid="request-data-fees-confirmed"
        isRequired
        label="I acknowledge and agree that CivilGrid will invoice me for any fees imposed by agencies for data requests"
        checked={formState.feesConfirmed}
        value={formState.feesConfirmed}
        onChange={(checked) => {
          store.formState.feesConfirmed = checked;
        }}
      />
      <div className="ml-6">
        <TableV2 id="utilities-fee-confirmation-v1" table={feeTable} />
      </div>
    </>
  );
}
